import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import Layout from '@components/Layout';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp,
  addDoc,
  increment,
} from 'firebase/firestore';
import { db } from '@root/firebase';
import { toast } from 'react-hot-toast';
import Modal from '@components/Modal';
import LikeButton from '@components/LikeButton';
import FavoriteButton from '@components/FavoriteButton';
import Comments from '@components/Comments';
import GetViews from '@components/GetViews';

// -----------------------------
// Firestore path constants
// -----------------------------
const FILES_COLLECTION = ['files', 'library', 'files']; // files/library/files
const COUNTERS_DOC = ['files', 'counters']; // files/counters
const SEARCH_DOC = ['search', 'library']; // search/library

// -----------------------------
// Utilities
// -----------------------------

// Firestore Timestamp or ISO/string → Date
const toDate = (v) => {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Safe string contains (case-insensitive)
const contains = (hay, needle) =>
  String(hay || '')
    .toLowerCase()
    .includes(String(needle || '').toLowerCase());

// Record a “preview” view under: files/library/files/<fileId>/views/<autoId>
// Also increments files/library/files/<fileId>.totalViews
async function recordLibraryPreviewView(fileId, userId) {
  if (!fileId) return;
  try {
    await addDoc(collection(db, ...FILES_COLLECTION, fileId, 'views'), {
      userId: userId || null,
      createdAt: serverTimestamp(),
    });
    await updateDoc(doc(db, ...FILES_COLLECTION, fileId), {
      totalViews: increment(1),
    });
  } catch (err) {
    console.warn('[recordLibraryPreviewView] failed:', err);
  }
}

function Library() {
  const { globalUser } = useAuth();

  // Sidebar + stats
  const [libraries, setLibraries] = useState([]);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [totalFileCount, setTotalFileCount] = useState(0);
  const [recentFileCount, setRecentFileCount] = useState(0);
  const [fileViewCount, setFileViewCount] = useState(0);
  const [recentlyViewed, setRecentlyViewed] = useState([]); // (kept in case you show later)
  const [topViewed, setTopViewed] = useState([]); // (kept in case you show later)

  // Search + results
  const [searchQuery, setSearchQuery] = useState('');
  const [resultsTitle, setResultsTitle] = useState('Recently Added Files');
  const [libraryNameResultsTitle, setLibraryNameResultsTitle] = useState('');
  const [subLibraryNameResultsTitle, setSubLibraryNameResultsTitle] =
    useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Preview modal
  const [selectedFileId, setSelectedFileId] = useState('');
  const [selectedFile, setSelectedFile] = useState('');
  const [selectedFileURL, setSelectedFileURL] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const RESULTS_PER_PAGE = 5;

  // -----------------------------
  // Initial load
  // -----------------------------
  const loadLibraryFilesSnapshot = useCallback(async () => {
    // 1) All files
    const filesSnap = await getDocs(collection(db, ...FILES_COLLECTION));
    const files = filesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // 2) Library view counter
    const countersDoc = await getDoc(doc(db, ...COUNTERS_DOC));
    const libraryViews = countersDoc.exists()
      ? countersDoc.data()?.libraryCount || 0
      : 0;

    // 3) Basic stats
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);

    const recentFiles = files
      .filter((f) => {
        const dt = toDate(f.upload_date);
        return dt && dt >= thirtyDaysAgo && dt <= now;
      })
      .sort((a, b) => toDate(b.upload_date) - toDate(a.upload_date));

    const recentlyViewedCalc = files
      .filter((f) => Array.isArray(f.viewed_by) && f.viewed_by.length > 0)
      .sort((a, b) => {
        const aLast =
          toDate(a.viewed_by?.[a.viewed_by.length - 1]?.timestamp) || 0;
        const bLast =
          toDate(b.viewed_by?.[b.viewed_by.length - 1]?.timestamp) || 0;
        return bLast - aLast;
      })
      .slice(0, 5);

    const topViewedCalc = files
      .filter((f) => typeof f.view_count === 'number')
      .sort((a, b) => (b.view_count || 0) - (a.view_count || 0))
      .slice(0, 5);

    // 4) folder -> direct file count
    const fileCountMap = files.reduce((acc, f) => {
      const folderId = f.folder_id;
      if (folderId) acc[folderId] = (acc[folderId] || 0) + 1;
      return acc;
    }, {});

    return {
      files,
      fileCountMap,
      totalFiles: files.length,
      recentFiles,
      libraryViews,
      topViewed: topViewedCalc,
      recentlyViewed: recentlyViewedCalc,
    };
  }, []);

  const buildLibrariesWithCounts = useCallback(async (fileCountMap) => {
    const snap = await getDocs(collection(db, 'library'));
    const libs = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      file_count: 0,
      sublibraries: [],
    }));

    // Build adjacency (parent → children)
    const byId = new Map();
    libs.forEach((l) => byId.set(l.id, { ...l, sublibraries: [] }));
    libs.forEach((l) => {
      const parent = l.parent;
      if (parent && byId.has(parent)) {
        byId.get(parent).sublibraries.push(byId.get(l.id));
      }
    });

    // Recursive total (direct count + descendants)
    const memo = new Map();
    const totalFor = (lib) => {
      if (memo.has(lib.id)) return memo.get(lib.id);
      const self = fileCountMap[lib.id] || 0;
      const sum = lib.sublibraries.reduce(
        (acc, sl) => acc + totalFor(sl),
        self
      );
      lib.file_count = sum;
      memo.set(lib.id, sum);
      return sum;
    };

    // Top-level only
    const topLevel = Array.from(byId.values()).filter((l) => !l.parent);
    topLevel.forEach(totalFor);

    // Sort by name
    topLevel.sort((a, b) =>
      (a.library_name || '').localeCompare(b.library_name || '')
    );
    return topLevel;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const {
          fileCountMap,
          totalFiles,
          recentFiles,
          libraryViews,
          topViewed,
          recentlyViewed,
        } = await loadLibraryFilesSnapshot();

        setTotalFileCount(totalFiles);
        setRecentFileCount(recentFiles.length);
        setFileViewCount(libraryViews);
        setTopViewed(topViewed);
        setRecentlyViewed(recentlyViewed);

        setSearchResults(recentFiles);
        setResultsTitle('Recently Added Files');
        setLibraryNameResultsTitle('');
        setSubLibraryNameResultsTitle('');
        setCurrentPage(1);

        const libs = await buildLibrariesWithCounts(fileCountMap);
        setLibraries(libs);
      } catch (err) {
        console.error('Error loading library:', err);
        toast.error('Failed to load library data.');
      }
    })();
  }, [loadLibraryFilesSnapshot, buildLibrariesWithCounts]);

  // -----------------------------
  // Search
  // -----------------------------
  const storeSearchQuery = useCallback(async (queryText) => {
    const q = (queryText || '').trim().toLowerCase();
    if (!q) return;
    try {
      const ref = doc(db, ...SEARCH_DOC);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        const newCount = (data[q] || 0) + 1;
        await updateDoc(ref, { [q]: newCount });
      } else {
        await setDoc(ref, { [q]: 1 });
      }
    } catch (error) {
      console.error('Error storing search query:', error);
    }
  }, []);

  const handleSearch = useCallback(
    async (e) => {
      e.preventDefault();
      const qText = searchQuery.trim();
      setLibraryNameResultsTitle('');
      setSubLibraryNameResultsTitle('');
      setResultsTitle(`Library Search for "${qText}"`);

      if (!qText) {
        setSearchResults([]);
        setCurrentPage(1);
        return;
      }

      await storeSearchQuery(qText);

      try {
        const snap = await getDocs(collection(db, ...FILES_COLLECTION));
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const qLower = qText.toLowerCase();

        const results = all.filter(
          (v) =>
            contains(v.file_name, qLower) ||
            contains(v.file_title, qLower) ||
            (Array.isArray(v.tags) && v.tags.some((t) => contains(t, qLower)))
        );

        setSearchResults(results);
        setCurrentPage(1);
      } catch (error) {
        console.error('Error searching files:', error);
        toast.error('An error occurred while searching the library.');
      }
    },
    [searchQuery, storeSearchQuery]
  );

  const searchFilesByFolderId = useCallback(async (folderId) => {
    if (!folderId) throw new Error('Invalid folderId');
    try {
      const qSnap = await getDocs(
        query(
          collection(db, ...FILES_COLLECTION),
          where('folder_id', '==', folderId)
        )
      );
      return qSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error searching files by folder_id:', error);
      throw new Error('Failed to search files');
    }
  }, []);

  const toggleAccordion = useCallback((libraryId) => {
    setActiveAccordion((prev) => (prev === libraryId ? null : libraryId));
  }, []);

  // -----------------------------
  // Pagination (derived)
  // -----------------------------
  const totalResults = searchResults.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / RESULTS_PER_PAGE));
  const indexOfLastResult = currentPage * RESULTS_PER_PAGE;
  const indexOfFirstResult = indexOfLastResult - RESULTS_PER_PAGE;

  const paginatedResults = useMemo(
    () => searchResults.slice(indexOfFirstResult, indexOfLastResult),
    [searchResults, indexOfFirstResult, indexOfLastResult]
  );

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 7;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    const left = Math.max(2, currentPage - 2);
    const right = Math.min(totalPages - 1, currentPage + 2);
    pages.push(1);
    if (left > 2) pages.push('…');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('…');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const showStart = totalResults === 0 ? 0 : indexOfFirstResult + 1;
  const showEnd = Math.min(indexOfLastResult, totalResults);

  // -----------------------------
  // File click → open preview + record view
  // -----------------------------
  const onPreviewClick = useCallback(
    (file) => {
      if (!file?.id) return toast.error('Invalid file.');
      setSelectedFileId(file.id);
      setSelectedFile(file.file_title || file.file_name || 'Untitled');
      setSelectedFileURL(file.file_url || '');
      if (globalUser?.uid) recordLibraryPreviewView(file.id, globalUser.uid);
    },
    [globalUser?.uid]
  );

  return (
    <Layout>
      <div className='row'>
        {/* Sidebar */}
        <div className='col-xl-3 col-lg-4'>
          {/* breadcrumbs */}
          <div className='row page-titles'>
            <div className='col-lg-12'>
              <ol className='breadcrumb'>
                <li
                  className='breadcrumb-item active'
                  style={{ fontSize: '16px' }}
                >
                  Resources
                </li>
                <li className='breadcrumb-item' style={{ fontSize: '16px' }}>
                  Library
                </li>
              </ol>
            </div>
          </div>

          {/* Menu */}
          <div className='card'>
            <div className='card-header bg-transparent'>
              <h5 className='mb-0'>Smart Library</h5>
            </div>
            <div className='card-body p-0'>
              <div className='mx-n3'>
                <div className='custom-accordion' id='libraryAccordion'>
                  {libraries.length > 0 ? (
                    libraries.map((library) => (
                      <div key={library.id}>
                        <a
                          className='text-body fw-medium d-flex align-items-center w-100 px-3 py-2 text-decoration-none'
                          href={`#collapse-${library.id}`}
                          role='button'
                          aria-expanded={activeAccordion === library.id}
                          aria-controls={`collapse-${library.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            toggleAccordion(library.id);
                            setLibraryNameResultsTitle(
                              library.library_name || ''
                            );
                            setSubLibraryNameResultsTitle('');
                          }}
                        >
                          <i
                            className={`mdi ${
                              activeAccordion === library.id
                                ? 'mdi-folder-open'
                                : 'mdi-folder'
                            } font-size-20 text-warning me-2`}
                          />
                          <span className='flex-grow-1 text-truncate'>
                            {library.library_name}
                          </span>
                          <i
                            className={`mdi mdi-chevron-${
                              activeAccordion === library.id ? 'down' : 'up'
                            } ms-auto`}
                          />
                          <span
                            className={`badge badge-xs ${
                              library.file_count > 0
                                ? 'badge-primary'
                                : 'badge-light'
                            } text-white badge-sm float-end"`}
                          >
                            {library.file_count || 0}
                          </span>
                        </a>

                        <div
                          className={`collapse ${
                            activeAccordion === library.id ? 'show' : ''
                          }`}
                          id={`collapse-${library.id}`}
                        >
                          <div className='card border-0 shadow-none ps-4 mb-0'>
                            <ul className='list-unstyled mb-0'>
                              {library.sublibraries?.length ? (
                                library.sublibraries.map((sublibrary) => (
                                  <li key={sublibrary.id}>
                                    <a
                                      href='#'
                                      className='links d-flex align-items-center w-100 px-3 py-2 text-decoration-none'
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        try {
                                          const results =
                                            await searchFilesByFolderId(
                                              sublibrary.id
                                            );
                                          setSearchResults(results);
                                          setCurrentPage(1);
                                          setSubLibraryNameResultsTitle(
                                            sublibrary.library_name || ''
                                          );
                                        } catch {
                                          toast.error(
                                            'Failed to fetch files for the selected folder.'
                                          );
                                        }
                                      }}
                                    >
                                      <i className='mdi mdi-subdirectory-arrow-right font-size-20 text-warning me-2' />
                                      <small className='text-truncate flex-grow-1'>
                                        {sublibrary.library_name}
                                        {sublibrary.file_count > 0 && (
                                          <span className='text-primary fst-italic small ms-1'>
                                            - {sublibrary.file_count}{' '}
                                            {sublibrary.file_count > 1
                                              ? 'files'
                                              : 'file'}
                                          </span>
                                        )}
                                      </small>
                                    </a>
                                  </li>
                                ))
                              ) : (
                                <li>
                                  <div className='d-flex align-items-center w-100 px-3 py-2'>
                                    <i className='mdi mdi-subdirectory-arrow-right font-size-20 text-warning me-2' />
                                    <small className='text-muted'>
                                      No Folders
                                    </small>
                                  </div>
                                </li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className='px-3 py-2 mb-0'>No libraries found.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className='col-lg-9'>
          <div className='row'>
            {/* Search */}
            <div className='col-xl-4 col-sm-6'>
              <div className='card'>
                <div className='card-body p-3'>
                  <form onSubmit={handleSearch}>
                    <div className='input-group'>
                      <input
                        type='text'
                        className='form-control'
                        id='txtSearch'
                        placeholder='Search the Library'
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <button className='btn btn-primary' type='submit'>
                        Search
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Recent File Count */}
            <div className='col-xl-4 col-sm-6'>
              <div className='card'>
                <div className='card-body p-3'>
                  <div className='d-flex align-items-center overflow-hidden'>
                    <div className='flex-shrink-0 me-3'>
                      <h1 className='fw-medium display-4 mb-0'>
                        {recentFileCount}
                      </h1>
                    </div>
                    <div className='flex-grow-1'>
                      <h5 className='font-size-15 mb-1 text-truncate'>
                        Recent Files
                      </h5>
                      <p className='text-muted font-size-11 text-truncate mb-0'>
                        Added in the last 30 days
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Total File Count */}
            <div className='col-xl-4 col-sm-6'>
              <div className='card'>
                <div className='card-body p-3'>
                  <div className='d-flex align-items-center overflow-hidden'>
                    <div className='flex-shrink-0 me-3'>
                      <h1 className='fw-medium display-4 mb-0'>
                        {totalFileCount}
                      </h1>
                    </div>
                    <div className='flex-grow-1'>
                      <h5 className='font-size-15 mb-1 text-truncate'>
                        Library Files
                      </h5>
                      <p className='text-muted font-size-11 text-truncate mb-0'>
                        SMART Library Files
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Search Results */}
          <div className='col-lg-12'>
            <div className='card'>
              <div className='card-header'>
                <h4 className='card-title'>
                  {libraryNameResultsTitle ? (
                    <>
                      {libraryNameResultsTitle}
                      {subLibraryNameResultsTitle ? (
                        <> / {subLibraryNameResultsTitle}</>
                      ) : null}
                    </>
                  ) : (
                    resultsTitle
                  )}
                </h4>
                <small className='text-muted'>
                  {`Showing ${Math.max(showStart, 0)}-${Math.max(
                    showEnd,
                    0
                  )} of ${totalResults} library files`}
                </small>
              </div>

              <div className='card-body'>
                <div className='table-responsive'>
                  <table className='table table-hover table-responsive-sm'>
                    <thead>
                      <tr>
                        <th style={{ fontSize: '14px' }}>#</th>
                        <th style={{ fontSize: '14px' }}>File title</th>
                        <th style={{ fontSize: '14px' }}>Size</th>
                        <th style={{ fontSize: '14px' }}>Date</th>
                        <th style={{ fontSize: '14px' }}>Preview</th>
                      </tr>
                    </thead>
                    <tbody id='smartLibraryResults'>
                      {paginatedResults.length > 0 ? (
                        paginatedResults.map((file, index) => (
                          <tr key={file.id}>
                            <th>{indexOfFirstResult + index + 1}</th>
                            <td
                              className='text-truncate'
                              title={file.file_title || file.file_name}
                            >
                              {file.file_title || file.file_name || 'Untitled'}
                            </td>
                            <td>
                              {file.file_size ? `${file.file_size}MB` : '—'}
                            </td>
                            <td>
                              {toDate(file.upload_date)
                                ? toDate(file.upload_date).toLocaleString()
                                : 'Unknown'}
                            </td>
                            <td>
                              <button
                                className='btn btn-link p-0 ms-auto'
                                onClick={(e) => {
                                  e.preventDefault();
                                  onPreviewClick(file);
                                }}
                                data-bs-toggle='modal'
                                data-bs-target='#previewLibraryFile'
                              >
                                <i className='fas fa-search' />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5}>
                            No results found. Try searching for something else.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <nav aria-label='Library pagination'>
                      <ul className='pagination pagination-sm pagination-circle'>
                        <li
                          className={`page-item page-indicator ${
                            currentPage === 1 ? 'disabled' : ''
                          }`}
                        >
                          <button
                            className='page-link'
                            onClick={() =>
                              setCurrentPage((p) => Math.max(1, p - 1))
                            }
                            disabled={currentPage === 1}
                          >
                            <i className='la la-angle-left'></i>
                          </button>
                        </li>

                        {pageNumbers.map((p, i) =>
                          p === '…' ? (
                            <li
                              key={`ellipsis-${i}`}
                              className='page-item disabled'
                            >
                              <span className='page-link'>…</span>
                            </li>
                          ) : (
                            <li
                              key={`p-${p}`}
                              className={`page-item ${
                                p === currentPage ? 'active' : ''
                              }`}
                            >
                              <button
                                className='page-link'
                                onClick={() => setCurrentPage(p)}
                              >
                                {p}
                              </button>
                            </li>
                          )
                        )}

                        <li
                          className={`page-item page-indicator ${
                            currentPage === totalPages ? 'disabled' : ''
                          }`}
                        >
                          <button
                            className='page-link'
                            onClick={() =>
                              setCurrentPage((p) => Math.min(totalPages, p + 1))
                            }
                            disabled={currentPage === totalPages}
                          >
                            <i className='la la-angle-right'></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* End Results */}
        </div>
      </div>

      {/* Preview Modal */}
      <Modal
        id='previewLibraryFile'
        title={selectedFile}
        size='xl'
        showFooter={false}
      >
        <div
          style={{
            height: '75vh',
            background: '#f9f9f9',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Top actions */}
          <div className='d-flex justify-content-between align-items-center p-2'>
            <button
              className='btn btn-sm btn-primary'
              onClick={() => window.open(selectedFileURL, '_blank')}
            >
              Open Fullscreen
            </button>

            <div className='d-flex justify-content-end align-items-center gap-3'>
              <GetViews docPath={`files/library/files/${selectedFileId}`} />
              {selectedFileId ? (
                <LikeButton
                  collectionPath='files/library/files'
                  documentId={selectedFileId}
                  userId={globalUser?.uid}
                  styled
                />
              ) : (
                <span className='opacity-0'>.</span>
              )}
              {selectedFileId ? (
                <FavoriteButton
                  collectionPath='files/library/files'
                  documentId={selectedFileId}
                  userId={globalUser?.uid}
                  styled
                />
              ) : (
                <span className='opacity-0'>.</span>
              )}
            </div>
          </div>

          {/* PDF Preview */}
          <iframe
            src={selectedFileURL}
            title='PDF Preview'
            style={{ flex: 1, width: '100%', border: 'none' }}
          />

          {/* Comments */}
          <div className='p-2'>
            <Comments
              parentId={selectedFileId}
              collectionPath='files/library/files'
              globalUser={globalUser?.uid}
              parentIdNull
            />
          </div>
        </div>
      </Modal>
    </Layout>
  );
}

export default Library;
